import { FC, useEffect, useState } from "react";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { Button } from "react-bootstrap";
import { notificationService } from "../../../../shared/services/notification-service";
import { initialResponseData } from "../../../../shared/utils/constants";
import { AxiosError } from "axios/index";
import InputSelect from "../../../../shared/components/atoms/input-select";
import DateRangeInput, {
  DateRangeInputChange,
} from "../../../../shared/components/atoms/date-range-input";
import { convertDateToString } from "../../../../shared/utils/functions-date-time";
import { BarChart, Bar, XAxis } from "recharts";
import {
  fetchDepartmentsOfficeNameOptions,
  fetchDivisionGroupOfficeNameOptions,
  fetchDepartmentsNameOptions,
  fetchPositionNameOptions,
  department_offices_name_Options,
  division_group_office_name_Options,
  departments_name_Options,
  position_name_Options,
} from "../../OptionsDropDown";
import { LeaveOnlineService } from "../../../../shared/services/Report/LeaveOnline/leave-online";

interface ReportSummaryInOutWorkData {
  count: {
    total: number;
    on_time: number;
    late: number;
  };
  rows: Array<{
    detail: {
      shift_name: string;
      work_time_start: string;
      work_time_end: string;
    };
    count1: {
      total: number;
      on_time: number;
      late: number;
      absent: number;
    };
    count2: {
      total: number;
      on_time: number;
      late: number;
      absent: number;
    };
  }>;
}

const ReportSummaryInOutWorkListPage: FC = () => {
  const [data, setData] = useState<ReportSummaryInOutWorkData>({
    count: {
      total: 0,
      on_time: 0,
      late: 0
    },
    rows: []
  });
  const [queryParams, setQueryParams] = useState({
    department_offices_name: "",
    division_group_office_name: "",
    structure_departments_name: "",
    position_name: "",
    date_start: "",
    date_end: "",
    search: "",
  });

  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();

  const fetchData = async () => {
    startLoading();
    try {
      const result = await LeaveOnlineService.getReportSummaryInOutWorkList(
        queryParams
      );
      setData(result);
    } catch (error) {
      const err = error as AxiosError;
      notificationService.dangerNotification(
        "โหลดข้อมูลไม่สำเร็จ",
        err.message
      );
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    queryParams.department_offices_name,
    queryParams.division_group_office_name,
    queryParams.structure_departments_name,
    queryParams.position_name,
    queryParams.date_start,
    queryParams.date_end,
  ]);

  useEffect(() => {
    fetchDepartmentsOfficeNameOptions();
    fetchDivisionGroupOfficeNameOptions();
    fetchDepartmentsNameOptions();
    fetchPositionNameOptions();
  }, []);

  const handleDateRangeChange = (value: DateRangeInputChange) => {
    setQueryParams({
      ...queryParams,
      date_start: value.startDate ? convertDateToString(value.startDate) : "",
      date_end: value.endDate ? convertDateToString(value.endDate) : "",
    });
  };

  function formatNumber(num: any) {
    if (Math.floor(num) === num) {
      return num.toFixed(0);
    } else {
      return num.toFixed(2);
    }
  }

  const CustomLabel = ({ x, y, width, value, total }: any) => (
    <text x={x + width / 2} y={y - 10} fill="#000" textAnchor="middle">
      <tspan x={x + width / 2} dy="-1.2em">{`${value} คน`}</tspan>
      <tspan x={x + width / 2} dy="1.2em">{`${
        total > 0 ? formatNumber((value * 100) / total) : 0
      } %`}</tspan>
    </text>
  );

  const CustomXAxisTick = ({ x, y, payload }: any) => {
    const words = payload.value.split("*");
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={15} textAnchor="middle" fill="#666">
          <tspan x="0" dy="0em">
            {words[0]}
          </tspan>
          <tspan x="0" dy="1.2em">
            {words[1]}
          </tspan>
        </text>
      </g>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">รายงานสรุปเข้าออกงาน</h2>
      </div>

      {loaderComponent}

      <div className="row">
        <div className="flex-row col-sm-12 col-md-3">
          <label className="form-label">สำนัก/ฝ่าย</label>
          <InputSelect
            value={queryParams.department_offices_name}
            options={department_offices_name_Options}
            placeholder=""
            name="is_active"
            onChange={(e: any) => {
              setQueryParams((prevState) => {
                return {
                  ...prevState,
                  department_offices_name: e.value.value || "",
                };
              });
            }}
            required={true}
            isValidateError={false}
          />
        </div>
        <div className="flex-row col-sm-12 col-md-3">
          <label className="form-label">กอง/กลุ่ม</label>
          <InputSelect
            value={queryParams.division_group_office_name}
            options={division_group_office_name_Options}
            placeholder=""
            name="is_active"
            onChange={(e: any) => {
              setQueryParams((prevState) => {
                return {
                  ...prevState,
                  division_group_office_name: e.value.value || "",
                };
              });
            }}
            required={true}
            isValidateError={false}
          />
        </div>
        <div className="flex-row col-sm-12 col-md-3">
          <label className="form-label">แผนก</label>
          <InputSelect
            value={queryParams.structure_departments_name}
            options={departments_name_Options}
            placeholder=""
            name="is_active"
            onChange={(e: any) => {
              setQueryParams((prevState) => {
                return {
                  ...prevState,
                  structure_departments_name: e.value.value || "",
                };
              });
            }}
            required={true}
            isValidateError={false}
          />
        </div>
        <div className="flex-row col-sm-12 col-md-3">
          <label className="form-label">ตำแหน่ง</label>
          <InputSelect
            value={queryParams.position_name}
            options={position_name_Options}
            placeholder=""
            name="is_active"
            onChange={(e: any) => {
              setQueryParams((prevState) => {
                return {
                  ...prevState,
                  position_name: e.value.value || "",
                };
              });
            }}
            required={true}
            isValidateError={false}
          />
        </div>
        <div className="flex-row col-sm-12 col-md-3">
          <label className="form-label">ช่วงวันเริ่มต้น-สิ้นสุด</label>
          <DateRangeInput
            startDate={
              queryParams.date_start ? new Date(queryParams.date_start) : null
            }
            endDate={
              queryParams.date_end ? new Date(queryParams.date_end) : null
            }
            onChange={handleDateRangeChange}
            disable={false}
          />
        </div>
        <div className="col-sm-12 col-md-3 d-flex align-items-end mt-4">
          <input
            className="form-control"
            type="text"
            value={queryParams.search as string}
            placeholder="ค้นหาจากชื่อกะ..."
            name="search"
            onChange={(e) => {
              setQueryParams((prevState) => {
                return {
                  ...prevState,
                  search: e.target.value || "",
                };
              });
            }}
          />
        </div>
        <div className="col-sm-12 col-md-1 d-flex align-items-end mt-3">
          <Button
            variant="primary"
            type="button"
            className="d-block px-4"
            onClick={fetchData}
          >
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </Button>
        </div>
      </div>

      <br />
      <br />

      <div className="d-flex  flex-wrap">
        {data.rows[0] && (
          <div key={1} className="mb-5 ">
            <h5 className="text-mof-primary m-0 text-center">
              {data.rows[0].detail?.shift_name}{" "}
              {data.rows[0].detail?.work_time_start.substring(0, 5)} -{" "}
              {data.rows[0].detail?.work_time_end.substring(0, 5)}
            </h5>
            <BarChart
              width={600}
              height={400}
              data={[
                {
                  name: "เข้างาน*(ปกติ)",
                  count: data.rows[0].count1.on_time || 0,
                },
                {
                  name: "สาย*(8:30 - 9:59)",
                  count: data.rows[0].count1.late || 0,
                },
                {
                  name: "ขาด*(เกิน 10:00)",
                  count: Math.abs(data.rows[0].count1.absent || 0),
                },
              ]}
              margin={{
                top: 70,
                bottom: 15,
                left: 100,
              }}
            >
              <XAxis dataKey="name" tick={<CustomXAxisTick />} />
              <Bar
                dataKey="count"
                barSize={50}
                fill="green"
                label={{
                  position: "top",
                  content: (props) => {
                    const { value } = props;
                    const total = 
                      (data.rows[0].count1.on_time || 0) + 
                      (data.rows[0].count1.late || 0) + 
                      Math.abs(data.rows[0].count1.absent || 0);
                    
                    return (
                      <CustomLabel 
                        {...props} 
                        total={total > 0 ? total : 1} 
                      />
                    );
                  },
                }}
              />
            </BarChart>
          </div>
        )}

        {data.rows[0] && (
          <div key={1} className="mb-5 ">
            <h5 className="text-mof-primary m-0 text-center">
            เวลาเข้า - ออกงานปกติ 08:45 - 16:45
            </h5>
            <BarChart
              width={600}
              height={400}
              data={[
                {
                  name: "เข้างาน*(ปกติ)",
                  count: data.rows[0].count2.on_time || 0,
                },
                {
                  name: "สาย*(08:45 - 09:59)",
                  count: data.rows[0].count2.late || 0,
                },
                {
                  name: "ขาด*(เกิน 10:00)",
                  count: Math.abs(data.rows[0].count2.absent || 0),
                },
              ]}
              margin={{
                top: 70,
                bottom: 15,
                left: 100,
              }}
            >
              <XAxis dataKey="name" tick={<CustomXAxisTick />} />
              <Bar
                dataKey="count"
                barSize={50}
                fill="green"
                label={{
                  position: "top",
                  content: (props) => {
                    const { value } = props;
                    const total = 
                      (data.rows[0].count2.on_time || 0) + 
                      (data.rows[0].count2.late || 0) + 
                      Math.abs(data.rows[0].count2.absent || 0);
                    
                    return (
                      <CustomLabel 
                        {...props} 
                        total={total > 0 ? total : 1} 
                      />
                    );
                  },
                }}
              />
            </BarChart>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportSummaryInOutWorkListPage;
